import React, { useMemo, useState } from "react";

type Address = {
  id: string;
  type: "Home" | "Office" | "Other";
  street: string;
  city: string;
  province: string;
  postalCode: string;
};

type Academic = {
  id: string;
  institution: string;
  degree: string;
  major: string;
  startYear: number;
  endYear?: number;
};

type UpdateHistory = {
  id: string;
  date: string;
  field: string;
  oldValue: string;
  newValue: string;
  updatedBy: string;
};

type Person = {
  id: string;
  name: string;
  birthdate: string;
  description: string;
  addresses: Address[];
  academics: Academic[];
  history: UpdateHistory[];
};

type ScalarField = "name" | "birthdate" | "description";

const initialMain: Person = {
  id: "P001",
  name: "John Alexander",
  birthdate: "1986-03-14",
  description:
    "Software engineer with experience in enterprise applications and data integration.",
  addresses: [
    {
      id: "A01",
      type: "Home",
      street: "Jl. Merdeka No. 15",
      city: "Bandung",
      province: "Jawa Barat",
      postalCode: "40111",
    },
    {
      id: "A02",
      type: "Office",
      street: "Jl. Asia Afrika No. 88",
      city: "Bandung",
      province: "Jawa Barat",
      postalCode: "40112",
    },
  ],
  academics: [
    {
      id: "EDU01",
      institution: "Institut Teknologi Bandung",
      degree: "Bachelor",
      major: "Computer Science",
      startYear: 2004,
      endYear: 2008,
    },
  ],
  history: [
    {
      id: "H01",
      date: "2026-08-01T10:30:00",
      field: "description",
      oldValue: "Software Engineer",
      newValue: "Senior Software Engineer",
      updatedBy: "Admin",
    },
  ],
};

const incomingData: Person = {
  id: "P002",
  name: "John A. Alexander",
  birthdate: "1986-03-14",
  description:
    "Senior software engineer specializing in React, Delphi, API development, and distributed systems.",
  addresses: [
    {
      id: "A10",
      type: "Home",
      street: "Jl. Merdeka No. 15",
      city: "Bandung",
      province: "Jawa Barat",
      postalCode: "40111",
    },
    {
      id: "A11",
      type: "Other",
      street: "Jl. Diponegoro No. 20",
      city: "Jakarta",
      province: "DKI Jakarta",
      postalCode: "10310",
    },
  ],
  academics: [
    {
      id: "EDU10",
      institution: "Institut Teknologi Bandung",
      degree: "Bachelor",
      major: "Informatics Engineering",
      startYear: 2004,
      endYear: 2008,
    },
    {
      id: "EDU11",
      institution: "Universitas Indonesia",
      degree: "Master",
      major: "Information Technology",
      startYear: 2011,
      endYear: 2013,
    },
  ],
  history: [
    {
      id: "H10",
      date: "2026-08-25T14:20:00",
      field: "address",
      oldValue: "Bandung",
      newValue: "Jakarta",
      updatedBy: "John",
    },
    {
      id: "H11",
      date: "2026-08-27T09:15:00",
      field: "description",
      oldValue: "Software Engineer",
      newValue: "Senior Software Engineer",
      updatedBy: "John",
    },
  ],
};

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

function formatDate(date: string) {
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(new Date(date));
}

function Badge({
  children,
  variant = "neutral",
}: {
  children: React.ReactNode;
  variant?: "neutral" | "different" | "same" | "new";
}) {
  const variants = {
    neutral: "bg-slate-100 text-slate-700",
    different: "bg-amber-100 text-amber-800",
    same: "bg-emerald-100 text-emerald-700",
    new: "bg-blue-100 text-blue-700",
  };

  return (
    <span
      className={cx(
        "inline-flex rounded-full px-2.5 py-1 text-xs font-medium",
        variants[variant],
      )}
    >
      {children}
    </span>
  );
}

function ActionButton({
  children,
  onClick,
  variant = "secondary",
}: {
  children: React.ReactNode;
  onClick: () => void;
  variant?: "primary" | "secondary" | "danger";
}) {
  const variants = {
    primary:
      "bg-indigo-600 text-white hover:bg-indigo-700 focus:ring-indigo-500",
    secondary:
      "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 focus:ring-slate-400",
    danger:
      "border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 focus:ring-red-400",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        "rounded-lg px-3 py-2 text-xs font-semibold transition",
        "focus:outline-none focus:ring-2 focus:ring-offset-2",
        variants[variant],
      )}
    >
      {children}
    </button>
  );
}

function CompareField({
  label,
  left,
  right,
  multiline = false,
  onReplace,
}: {
  label: string;
  left: string;
  right: string;
  multiline?: boolean;
  onReplace: () => void;
}) {
  const different = left !== right;

  return (
    <div className="border-b border-slate-200 py-5 last:border-b-0">
      <div className="mb-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-slate-700">{label}</span>
          <Badge variant={different ? "different" : "same"}>
            {different ? "Different" : "Same"}
          </Badge>
        </div>

        {different && (
          <ActionButton onClick={onReplace}>← Use incoming</ActionButton>
        )}
      </div>

      <div className="grid gap-3 lg:grid-cols-2">
        <div
          className={cx(
            "rounded-xl border p-4",
            different
              ? "border-amber-200 bg-amber-50/50"
              : "border-slate-200 bg-white",
          )}
        >
          <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">
            Main
          </div>
          <div
            className={cx(
              "text-sm text-slate-800",
              multiline && "whitespace-pre-wrap leading-6",
            )}
          >
            {left || "—"}
          </div>
        </div>

        <div
          className={cx(
            "rounded-xl border p-4",
            different
              ? "border-blue-200 bg-blue-50/50"
              : "border-slate-200 bg-white",
          )}
        >
          <div className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-400">
            Incoming
          </div>
          <div
            className={cx(
              "text-sm text-slate-800",
              multiline && "whitespace-pre-wrap leading-6",
            )}
          >
            {right || "—"}
          </div>
        </div>
      </div>
    </div>
  );
}

function SectionHeader({
  title,
  leftCount,
  rightCount,
  onMerge,
  onReplace,
}: {
  title: string;
  leftCount: number;
  rightCount: number;
  onMerge: () => void;
  onReplace: () => void;
}) {
  return (
    <div className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
      <div>
        <h2 className="font-semibold text-slate-900">{title}</h2>
        <p className="mt-0.5 text-xs text-slate-500">
          Main: {leftCount} · Incoming: {rightCount}
        </p>
      </div>

      <div className="flex gap-2">
        <ActionButton onClick={onMerge}>Merge all</ActionButton>
        <ActionButton onClick={onReplace}>Replace section</ActionButton>
      </div>
    </div>
  );
}

export default function JsonCompareMergePage() {
  const [main, setMain] = useState<Person>(initialMain);
  const incoming = incomingData;

  const [message, setMessage] = useState<string>("");

  const differenceCount = useMemo(() => {
    let count = 0;

    if (main.name !== incoming.name) count++;
    if (main.birthdate !== incoming.birthdate) count++;
    if (main.description !== incoming.description) count++;

    count += incoming.addresses.filter(
      (incomingItem) =>
        !main.addresses.some(
          (mainItem) =>
            mainItem.street === incomingItem.street &&
            mainItem.city === incomingItem.city,
        ),
    ).length;

    count += incoming.academics.filter(
      (incomingItem) =>
        !main.academics.some(
          (mainItem) =>
            mainItem.institution === incomingItem.institution &&
            mainItem.degree === incomingItem.degree,
        ),
    ).length;

    return count;
  }, [main, incoming]);

  function notify(text: string) {
    setMessage(text);

    window.setTimeout(() => {
      setMessage("");
    }, 2500);
  }

  function replaceScalar(field: ScalarField) {
    setMain((current) => ({
      ...current,
      [field]: incoming[field],
    }));

    notify(`${field} replaced with incoming value.`);
  }

  function mergeAddresses() {
    setMain((current) => {
      const result = [...current.addresses];

      incoming.addresses.forEach((item) => {
        const exists = result.some(
          (x) => x.street === item.street && x.city === item.city,
        );

        if (!exists) result.push(item);
      });

      return {
        ...current,
        addresses: result,
      };
    });

    notify("Incoming addresses merged.");
  }

  function replaceAddresses() {
    setMain((current) => ({
      ...current,
      addresses: [...incoming.addresses],
    }));

    notify("Addresses replaced.");
  }

  function addAddress(address: Address) {
    setMain((current) => ({
      ...current,
      addresses: current.addresses.some(
        (x) => x.street === address.street && x.city === address.city,
      )
        ? current.addresses
        : [...current.addresses, address],
    }));

    notify("Address merged.");
  }

  function replaceAddress(address: Address) {
    setMain((current) => {
      const sameTypeIndex = current.addresses.findIndex(
        (x) => x.type === address.type,
      );

      if (sameTypeIndex === -1) {
        return {
          ...current,
          addresses: [...current.addresses, address],
        };
      }

      const addresses = [...current.addresses];
      addresses[sameTypeIndex] = address;

      return {
        ...current,
        addresses,
      };
    });

    notify(`${address.type} address replaced.`);
  }

  function mergeAcademics() {
    setMain((current) => {
      const result = [...current.academics];

      incoming.academics.forEach((item) => {
        const exists = result.some(
          (x) =>
            x.institution === item.institution &&
            x.degree === item.degree &&
            x.startYear === item.startYear,
        );

        if (!exists) result.push(item);
      });

      return {
        ...current,
        academics: result,
      };
    });

    notify("Academic records merged.");
  }

  function replaceAcademics() {
    setMain((current) => ({
      ...current,
      academics: [...incoming.academics],
    }));

    notify("Academic records replaced.");
  }

  function addAcademic(academic: Academic) {
    setMain((current) => ({
      ...current,
      academics: current.academics.some(
        (x) =>
          x.institution === academic.institution &&
          x.degree === academic.degree &&
          x.startYear === academic.startYear,
      )
        ? current.academics
        : [...current.academics, academic],
    }));

    notify("Academic record merged.");
  }

  function replaceAcademic(academic: Academic) {
    setMain((current) => {
      const index = current.academics.findIndex(
        (x) => x.institution === academic.institution,
      );

      if (index === -1) {
        return {
          ...current,
          academics: [...current.academics, academic],
        };
      }

      const academics = [...current.academics];
      academics[index] = academic;

      return {
        ...current,
        academics,
      };
    });

    notify("Academic record replaced.");
  }

  function mergeHistory() {
    setMain((current) => {
      const existing = new Set(current.history.map((item) => item.id));

      return {
        ...current,
        history: [
          ...current.history,
          ...incoming.history.filter((item) => !existing.has(item.id)),
        ],
      };
    });

    notify("Update histories merged.");
  }

  function replaceHistory() {
    setMain((current) => ({
      ...current,
      history: [...incoming.history],
    }));

    notify("Update history replaced.");
  }

  function mergeEverything() {
    setMain((current) => {
      const addresses = [...current.addresses];

      incoming.addresses.forEach((item) => {
        if (
          !addresses.some(
            (x) => x.street === item.street && x.city === item.city,
          )
        ) {
          addresses.push(item);
        }
      });

      const academics = [...current.academics];

      incoming.academics.forEach((item) => {
        if (
          !academics.some(
            (x) =>
              x.institution === item.institution &&
              x.degree === item.degree &&
              x.startYear === item.startYear,
          )
        ) {
          academics.push(item);
        }
      });

      const historyIds = new Set(current.history.map((item) => item.id));

      return {
        ...current,
        addresses,
        academics,
        history: [
          ...current.history,
          ...incoming.history.filter((item) => !historyIds.has(item.id)),
        ],
      };
    });

    notify("All array records merged.");
  }

  function replaceEverything() {
    setMain(structuredClone(incoming));
    notify("Main object replaced with incoming object.");
  }

  function reset() {
    setMain(structuredClone(initialMain));
    notify("Changes reset.");
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-col gap-4 px-4 py-4 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold tracking-tight">
                Record Comparison
              </h1>

              {differenceCount > 0 && (
                <Badge variant="different">
                  {differenceCount} differences
                </Badge>
              )}
            </div>

            <p className="mt-1 text-sm text-slate-500">
              Compare incoming data and merge selected values into the main
              record.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <ActionButton onClick={reset}>Reset</ActionButton>

            <ActionButton onClick={mergeEverything}>
              Merge all items
            </ActionButton>

            <ActionButton onClick={replaceEverything} variant="primary">
              Replace all
            </ActionButton>
          </div>
        </div>
      </header>

      {/* Toast */}
      {message && (
        <div className="fixed right-4 top-24 z-50 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 shadow-lg">
          ✓ {message}
        </div>
      )}

      <main className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        {/* Column labels */}
        <div className="hidden grid-cols-2 gap-4 lg:grid">
          <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-5 py-4">
            <div className="text-xs font-bold uppercase tracking-wider text-indigo-600">
              Main Record
            </div>
            <div className="mt-1 text-sm text-indigo-900">
              Current data that will be preserved or modified.
            </div>
          </div>

          <div className="rounded-xl border border-blue-200 bg-blue-50 px-5 py-4">
            <div className="text-xs font-bold uppercase tracking-wider text-blue-600">
              Incoming Record
            </div>
            <div className="mt-1 text-sm text-blue-900">
              New data available for merging or replacement.
            </div>
          </div>
        </div>

        {/* Basic fields */}
        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 px-5 py-4">
            <h2 className="font-semibold">Basic Information</h2>
          </div>

          <div className="px-5">
            <CompareField
              label="Name"
              left={main.name}
              right={incoming.name}
              onReplace={() => replaceScalar("name")}
            />

            <CompareField
              label="Birthdate"
              left={formatDate(main.birthdate)}
              right={formatDate(incoming.birthdate)}
              onReplace={() => replaceScalar("birthdate")}
            />

            <CompareField
              label="Description"
              left={main.description}
              right={incoming.description}
              multiline
              onReplace={() => replaceScalar("description")}
            />
          </div>
        </section>

        {/* Addresses */}
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <SectionHeader
            title="Addresses"
            leftCount={main.addresses.length}
            rightCount={incoming.addresses.length}
            onMerge={mergeAddresses}
            onReplace={replaceAddresses}
          />

          <div className="grid lg:grid-cols-2 lg:divide-x lg:divide-slate-200">
            <div className="space-y-3 bg-slate-50/40 p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-400">
                Main
              </div>

              {main.addresses.map((address) => (
                <AddressCard key={address.id} address={address} />
              ))}
            </div>

            <div className="space-y-3 border-t border-slate-200 bg-blue-50/20 p-5 lg:border-t-0">
              <div className="text-xs font-bold uppercase tracking-wider text-blue-500">
                Incoming
              </div>

              {incoming.addresses.map((address) => {
                const exists = main.addresses.some(
                  (x) =>
                    x.street === address.street && x.city === address.city,
                );

                return (
                  <AddressCard
                    key={address.id}
                    address={address}
                    status={exists ? "same" : "new"}
                    actions={
                      <>
                        {!exists && (
                          <ActionButton onClick={() => addAddress(address)}>
                            + Merge
                          </ActionButton>
                        )}

                        <ActionButton
                          onClick={() => replaceAddress(address)}
                          variant="primary"
                        >
                          ← Replace {address.type}
                        </ActionButton>
                      </>
                    }
                  />
                );
              })}
            </div>
          </div>
        </section>

        {/* Academics */}
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <SectionHeader
            title="Academic History"
            leftCount={main.academics.length}
            rightCount={incoming.academics.length}
            onMerge={mergeAcademics}
            onReplace={replaceAcademics}
          />

          <div className="grid lg:grid-cols-2 lg:divide-x lg:divide-slate-200">
            <div className="space-y-3 bg-slate-50/40 p-5">
              <div className="text-xs font-bold uppercase tracking-wider text-slate-400">
                Main
              </div>

              {main.academics.map((academic) => (
                <AcademicCard key={academic.id} academic={academic} />
              ))}
            </div>

            <div className="space-y-3 border-t border-slate-200 bg-blue-50/20 p-5 lg:border-t-0">
              <div className="text-xs font-bold uppercase tracking-wider text-blue-500">
                Incoming
              </div>

              {incoming.academics.map((academic) => {
                const exact = main.academics.some(
                  (x) =>
                    x.institution === academic.institution &&
                    x.degree === academic.degree &&
                    x.major === academic.major,
                );

                return (
                  <AcademicCard
                    key={academic.id}
                    academic={academic}
                    status={exact ? "same" : "new"}
                    actions={
                      <>
                        {!exact && (
                          <ActionButton onClick={() => addAcademic(academic)}>
                            + Merge
                          </ActionButton>
                        )}

                        <ActionButton
                          onClick={() => replaceAcademic(academic)}
                          variant="primary"
                        >
                          ← Replace
                        </ActionButton>
                      </>
                    }
                  />
                );
              })}
            </div>
          </div>
        </section>

        {/* History */}
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <SectionHeader
            title="Update History"
            leftCount={main.history.length}
            rightCount={incoming.history.length}
            onMerge={mergeHistory}
            onReplace={replaceHistory}
          />

          <div className="grid lg:grid-cols-2 lg:divide-x lg:divide-slate-200">
            <HistoryList title="Main" items={main.history} />

            <HistoryList
              title="Incoming"
              items={incoming.history}
              incoming
            />
          </div>
        </section>

        {/* Result */}
        <details className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <summary className="cursor-pointer px-5 py-4 font-semibold">
            Preview Result JSON
          </summary>

          <div className="border-t border-slate-200 bg-slate-950 p-5">
            <pre className="overflow-x-auto text-xs leading-6 text-slate-200">
              {JSON.stringify(main, null, 2)}
            </pre>
          </div>
        </details>
      </main>
    </div>
  );
}

function AddressCard({
  address,
  status,
  actions,
}: {
  address: Address;
  status?: "same" | "new";
  actions?: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="font-semibold">{address.type}</span>

            {status && (
              <Badge variant={status}>
                {status === "same" ? "Already exists" : "New"}
              </Badge>
            )}
          </div>

          <div className="mt-2 space-y-1 text-sm text-slate-600">
            <div>{address.street}</div>
            <div>
              {address.city}, {address.province}
            </div>
            <div>{address.postalCode}</div>
          </div>
        </div>
      </div>

      {actions && (
        <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
          {actions}
        </div>
      )}
    </div>
  );
}

function AcademicCard({
  academic,
  status,
  actions,
}: {
  academic: Academic;
  status?: "same" | "new";
  actions?: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold">{academic.institution}</span>

            {status && (
              <Badge variant={status}>
                {status === "same" ? "Same" : "New / Different"}
              </Badge>
            )}
          </div>

          <div className="mt-2 text-sm font-medium text-slate-700">
            {academic.degree} · {academic.major}
          </div>

          <div className="mt-1 text-sm text-slate-500">
            {academic.startYear} – {academic.endYear ?? "Present"}
          </div>
        </div>
      </div>

      {actions && (
        <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-3">
          {actions}
        </div>
      )}
    </div>
  );
}

function HistoryList({
  title,
  items,
  incoming = false,
}: {
  title: string;
  items: UpdateHistory[];
  incoming?: boolean;
}) {
  return (
    <div
      className={cx(
        "space-y-4 p-5",
        incoming ? "bg-blue-50/20" : "bg-slate-50/40",
      )}
    >
      <div
        className={cx(
          "text-xs font-bold uppercase tracking-wider",
          incoming ? "text-blue-500" : "text-slate-400",
        )}
      >
        {title}
      </div>

      <div className="relative space-y-5 border-l border-slate-200 pl-5">
        {items.map((item) => (
          <div key={item.id} className="relative">
            <div className="absolute -left-6.25 top-1.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-slate-400 ring-1 ring-slate-300" />

            <div className="text-xs font-medium text-slate-400">
              {new Date(item.date).toLocaleString()}
            </div>

            <div className="mt-1 text-sm font-semibold text-slate-800">
              {item.field}
            </div>

            <div className="mt-2 rounded-lg bg-slate-50 p-3 text-xs">
              <div className="text-red-600">
                <span className="font-semibold">− </span>
                {item.oldValue}
              </div>

              <div className="mt-1 text-emerald-700">
                <span className="font-semibold">+ </span>
                {item.newValue}
              </div>
            </div>

            <div className="mt-2 text-xs text-slate-400">
              Updated by {item.updatedBy}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}